/**
 * ForgotPasswordPage — split layout, marketing card on the right.
 *
 * Three-step internal wizard (URL-stable so a refresh doesn't lose state
 * mid-flow):
 *   step='request' → enter email → POST /api/password-reset-request/
 *   step='confirm' → enter OTP + new password → POST /api/password-reset-confirm/
 *   step='success' → link back to login
 *
 * Visual treatment: matches the Figma — large display-serif heading,
 * thin subtitle, single glass input, teal CTA. Subsequent steps reuse
 * the same shell but swap the form body.
 *
 * Backend contract unchanged.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  HiOutlineLockClosed,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineCheckCircle,
} from 'react-icons/hi2'
import { authService } from '../../services/authService'
import { Button, FormField, Input, IconButton, Banner } from '../../components/ui'
import {
  AuthShell,
  AuthHeading,
  OTPInput,
} from '../../components/auth'

// ─── Schemas ─────────────────────────────────────────────────────────────

const requestSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
})

const confirmSchema = z.object({
  new_password: z
    .string()
    .min(1, 'Password is required')
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Must contain upper, lower, and a number'),
  confirm_password: z.string().min(1, 'Please confirm your password'),
}).refine((d) => d.new_password === d.confirm_password, {
  message: "Passwords don't match",
  path: ['confirm_password'],
})

// ─── Page ────────────────────────────────────────────────────────────────

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [step, setStep] = useState('request')
  const [email, setEmail] = useState('')
  const [apiError, setApiError] = useState('')
  const [info, setInfo] = useState('')

  return (
    <AuthShell
      variant="split"
      showBack
      backTo="/login"
    >
      <AuthHeading
        title={
          step === 'request' ? t('auth.forgot.title')
          : step === 'confirm' ? t('auth.otp.title')
          : 'Password reset'
        }
        subtitle={
          step === 'request' ? t('auth.forgot.subtitle')
          : step === 'confirm' ? (<>
              {t('auth.otp.subtitlePrefix')}{' '}
              <span className="font-semibold text-ink-primary">{email}</span>
            </>)
          : "Your password has been reset. You can sign in now."
        }
      />

      {step === 'request' && (
        <RequestStep
          initialEmail={email}
          apiError={apiError}
          onError={setApiError}
          onSent={(value) => {
            setEmail(value)
            setApiError('')
            setInfo('Reset code sent — check your email.')
            setStep('confirm')
          }}
        />
      )}

      {step === 'confirm' && (
        <ConfirmStep
          email={email}
          info={info}
          apiError={apiError}
          onError={setApiError}
          onClearError={() => setApiError('')}
          onSuccess={() => setStep('success')}
          onResend={async () => {
            setApiError(''); setInfo('')
            try {
              const res = await authService.requestPasswordReset(email)
              if (res?.success) setInfo('A new code has been sent — check your email.')
              else setApiError(res?.error?.message || res?.data?.message || 'Failed to resend code')
            } catch { setApiError('Network error — please try again') }
          }}
          onBack={() => { setApiError(''); setInfo(''); setStep('request') }}
        />
      )}

      {step === 'success' && <SuccessStep onContinue={() => navigate('/login', { replace: true })} />}
    </AuthShell>
  )
}

// ─── Step 1: request reset code ───────────────────────────────────────────

function RequestStep({ initialEmail, onSent, onError, apiError }) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: initialEmail || '' },
    mode: 'onBlur',
  })

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      const res = await authService.requestPasswordReset(data.email)
      if (res?.success) onSent(data.email)
      else onError(res?.error?.message || res?.data?.message || 'Failed to send reset code')
    } catch { onError('Network error — please try again') }
    finally { setIsLoading(false) }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {apiError && <Banner tone="danger">{apiError}</Banner>}

      <FormField
        label={t('auth.forgot.fieldLabel')}
        error={errors.email?.message}
      >
        <Input
          type="email"
          autoComplete="email"
          placeholder={t('auth.forgot.fieldPlaceholder')}
          size="lg"
          {...register('email')}
        />
      </FormField>

      <div className="pt-8">
        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={isLoading}
        >
          {t('auth.forgot.submit')}
        </Button>
      </div>
    </form>
  )
}

// ─── Step 2: enter code + new password ────────────────────────────────────

function ConfirmStep({ email, info, apiError, onError, onClearError, onResend, onBack, onSuccess }) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [code, setCode] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [showPw2, setShowPw2] = useState(false)
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(confirmSchema),
    mode: 'onBlur',
  })

  const onSubmit = async (data) => {
    onClearError()
    if (!/^\d{4,8}$/.test(code)) {
      onError('Enter the 4-8 digit code we sent you')
      return
    }
    setIsLoading(true)
    try {
      const res = await authService.confirmPasswordReset(email, code, data.new_password)
      if (res?.success) onSuccess()
      else onError(res?.error?.message || res?.data?.message || 'Failed to reset password — check the code and try again')
    } catch { onError('Network error — please try again') }
    finally { setIsLoading(false) }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {info && !apiError && <Banner tone="info">{info}</Banner>}
      {apiError && <Banner tone="danger">{apiError}</Banner>}

      <div className="flex justify-center">
        <OTPInput value={code} onChange={setCode} length={6} />
      </div>

      <FormField
        label="New password"
        error={errors.new_password?.message}
      >
        <Input
          type={showPw ? 'text' : 'password'}
          autoComplete="new-password"
          placeholder="Create a strong password"
          leadingIcon={HiOutlineLockClosed}
          trailingAction={
            <IconButton
              icon={showPw ? HiOutlineEyeSlash : HiOutlineEye}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              variant="ghost"
              size="sm"
              onClick={() => setShowPw(s => !s)}
            />
          }
          {...register('new_password')}
        />
      </FormField>

      <FormField
        label="Confirm new password"
        error={errors.confirm_password?.message}
      >
        <Input
          type={showPw2 ? 'text' : 'password'}
          autoComplete="new-password"
          placeholder="Re-enter the new password"
          leadingIcon={HiOutlineLockClosed}
          trailingAction={
            <IconButton
              icon={showPw2 ? HiOutlineEyeSlash : HiOutlineEye}
              aria-label={showPw2 ? 'Hide password' : 'Show password'}
              variant="ghost"
              size="sm"
              onClick={() => setShowPw2(s => !s)}
            />
          }
          {...register('confirm_password')}
        />
      </FormField>

      <div className="flex items-center justify-center gap-1 text-sm">
        <span className="text-ink-secondary">{t('auth.otp.didntReceive')}</span>{' '}
        <button
          type="button"
          disabled={resending}
          onClick={async () => { setResending(true); await onResend(); setResending(false) }}
          className="font-semibold text-brand hover:text-brand-hover transition-colors disabled:opacity-50"
        >
          {t('auth.otp.resendCode')}
        </button>
      </div>

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={isLoading}
      >
        Reset password
      </Button>

      <button
        type="button"
        onClick={onBack}
        className="block mx-auto text-xs text-ink-muted hover:text-ink-secondary transition-colors"
      >
        Use a different email
      </button>
    </form>
  )
}

// ─── Step 3: success ──────────────────────────────────────────────────────

function SuccessStep({ onContinue }) {
  return (
    <div className="text-center space-y-6">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-success-soft text-success">
        <HiOutlineCheckCircle className="w-9 h-9" />
      </div>
      <p className="text-sm text-ink-secondary">
        Your password has been reset successfully.
      </p>
      <Button
        size="lg"
        fullWidth
        onClick={onContinue}
      >
        Go to sign in
      </Button>
    </div>
  )
}
